import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { ROLES, SERVICES } from "../../data/content";
import { Field, TextInput, TextArea, Choice, Honeypot, FormStatus } from "./Fields";

const EMPTY = { name: "", email: "", phone: "", role: "", organization: "", service: "", delivery: "Not sure yet", location: "", message: "", website: "" };

export const ConsultationForm = ({ defaultRole = "", defaultService = "" }) => {
  const [form, setForm] = useState({ ...EMPTY, role: defaultRole, service: defaultService });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.role) {
      setStatus({ type: "error", title: "Please choose your role", text: "Let me know whether you're a school, family, educator or other professional." });
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      await api.post("/consultations", form);
      setStatus({ type: "success", title: "Thank you, your consultation request was sent.", text: `Michelle will reply to ${form.email} soon. You can also book a time on the booking page.` });
      toast.success("Consultation request sent");
      setForm({ ...EMPTY });
    } catch (err) {
      const msg = errorMessage(err);
      setStatus({ type: "error", title: "Your request wasn't sent", text: msg });
      toast.error("Request not sent", { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} aria-label="Consultation request" className="space-y-6" data-testid="consultation-form" noValidate={false}>
      <div className="grid sm:grid-cols-2 gap-6">
        <Field id="c-name" label="Name" required><TextInput id="c-name" autoComplete="name" required value={form.name} onChange={set("name")} /></Field>
        <Field id="c-email" label="Email" required><TextInput id="c-email" type="email" autoComplete="email" required value={form.email} onChange={set("email")} /></Field>
        <Field id="c-role" label="Your role" required><Choice id="c-role" value={form.role} onChange={set("role")} options={ROLES} required /></Field>
        <Field id="c-phone" label="Phone"><TextInput id="c-phone" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} /></Field>
        <Field id="c-organization" label="School or organization"><TextInput id="c-organization" autoComplete="organization" value={form.organization} onChange={set("organization")} /></Field>
        <Field id="c-location" label="Your location" hint="City, state or country"><TextInput id="c-location" value={form.location} onChange={set("location")} aria-describedby="c-location-hint" /></Field>
        <Field id="c-service" label="Service of interest"><Choice id="c-service" value={form.service} onChange={set("service")} options={[...SERVICES.map((s) => s.title), "Guest speaking", "Not sure yet"]} /></Field>
        <Field id="c-delivery" label="Preferred format" required><Choice id="c-delivery" value={form.delivery} onChange={set("delivery")} options={["In person", "Virtual", "Not sure yet"]} /></Field>
      </div>
      <Field id="c-message" label="How can I help?" required>
        <TextArea id="c-message" rows={4} required value={form.message} onChange={set("message")} />
      </Field>
      <Honeypot value={form.website} onChange={set("website")} />
      <button type="submit" disabled={busy} className="group inline-flex items-center gap-3 font-sans bg-teal text-paper rounded-full px-8 py-4 hover:bg-teal-deep transition-colors disabled:opacity-70" data-testid="consultation-submit-button">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
        {busy ? "Sending..." : "Request a consultation"}
        {!busy && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />}
      </button>
      <FormStatus status={status} testid="consultation-form-status" />
    </form>
  );
};
